<template>
  <div class="gatehouse-page">
    <section class="resource-toolbar">
      <div>
        <h2>{{ activeSectionMeta.title }}</h2>
        <p>{{ activeSectionMeta.description }}</p>
      </div>
      <v-btn v-if="activeSection === 'autorizacoes'" text color="primary" @click="openAuthorizationRequestDialog()">
        <v-icon left size="18">mdi-bell-ring-outline</v-icon>
        Solicitar aprovação
      </v-btn>
      <v-btn v-if="activeSection === 'portarias'" text color="primary" @click="openGatehouseDialog()">
        <v-icon left size="18">mdi-plus</v-icon>
        Adicionar
      </v-btn>
      <v-btn v-if="activeSection === 'encomendas'" text color="primary" @click="openPackageDialog()">
        <v-icon left size="18">mdi-package-variant-closed</v-icon>
        Adicionar
      </v-btn>
    </section>

    <ListFilters
      v-model="filterDraft"
      :fields="filterFields"
      :loading="activeLoading"
      @filter="applyFilters"
      @clear="applyFilters"
    />

    <div class="tabs-body">
      <template v-if="activeSection === 'autorizacoes'">
        <section class="table-panel">
          <BoaruaDataTable :headers="authorizationHeaders" :items="filteredAuthorizations" :loading="loadingAuthorizations" class="boarua-table">
            <template v-slot:item.member="{ item }">
              {{ item.member && item.member.user ? item.member.user.name : "-" }}
            </template>
            <template v-slot:item.access_code="{ item }">
              <strong v-if="item.status !== 'pending_approval' && item.status !== 'rejected'">{{ item.access_code }}</strong>
              <span v-else>-</span>
            </template>
            <template v-slot:item.status="{ item }">
              <span class="status-pill" :class="`status-${item.status || 'authorized'}`">
                {{ formatAuthorizationStatus(item.status) }}
              </span>
            </template>
            <template v-slot:item.actions="{ item }">
              <template v-if="item.status === 'pending_approval'">
                <span class="muted-action">Aguardando morador</span>
              </template>
              <template v-else-if="item.status === 'rejected'">
                <span class="muted-action">Rejeitada</span>
              </template>
              <template v-else>
                <v-btn x-small depressed color="primary" @click="registerAccess(item, 'entry')">Entrada</v-btn>
                <v-btn x-small outlined color="primary" @click="registerAccess(item, 'exit')">Saída</v-btn>
              </template>
            </template>
            <template v-slot:footer.prepend>
              <v-btn icon small :loading="loadingAuthorizations" title="Atualizar" @click="loadAuthorizations">
                <v-icon size="19">mdi-refresh</v-icon>
              </v-btn>
            </template>
          </BoaruaDataTable>
        </section>
      </template>

      <template v-if="activeSection === 'encomendas'">
        <section class="table-panel">
          <BoaruaDataTable :headers="packageHeaders" :items="filteredPackages" :loading="loadingPackages" class="boarua-table">
            <template v-slot:item.unit="{ item }">
              {{ unitLabel(item.unit) }}
            </template>
            <template v-slot:item.recipient_name="{ item }">
              {{ item.recipient_name || "-" }}
            </template>
            <template v-slot:item.received_at="{ item }">
              {{ item.received_at | defaultDate }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn v-if="item.status !== 'picked_up'" x-small depressed color="primary" @click="pickupPackage(item)">
                Retirada
              </v-btn>
              <v-btn icon small @click="openPackageDialog(item)">
                <v-icon size="19">mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <template v-slot:footer.prepend>
              <v-btn icon small :loading="loadingPackages" title="Atualizar" @click="loadPackages">
                <v-icon size="19">mdi-refresh</v-icon>
              </v-btn>
            </template>
          </BoaruaDataTable>
        </section>
      </template>

      <template v-if="activeSection === 'portarias'">
        <section class="table-panel">
          <BoaruaDataTable :headers="gatehouseHeaders" :items="filteredGatehouses" :loading="loadingGatehouses" item-key="id" show-expand single-expand class="boarua-table">
            <template v-slot:item.contributionGroup="{ item }">
              {{ item.contributionGroup ? item.contributionGroup.name : "Local" }}
            </template>
            <template v-slot:item.users="{ item }">
              {{ item.users ? item.users.length : 0 }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small title="Adicionar operador" @click="openOperatorDialog(item)">
                <v-icon size="19">mdi-account-plus-outline</v-icon>
              </v-btn>
              <v-btn icon small @click="openGatehouseDialog(item)">
                <v-icon size="19">mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="expanded-cell">
                <div class="operator-box">
                  <div class="operator-box-header">
                    <strong>Operadores</strong>
                    <v-btn small depressed color="primary" @click="openOperatorDialog(item)">
                      <v-icon left size="17">mdi-account-plus-outline</v-icon>
                      Adicionar
                    </v-btn>
                  </div>
                  <div v-if="item.users && item.users.length" class="operator-list">
                    <div v-for="link in item.users" :key="link.id" class="operator-row">
                      <div>
                        <strong>{{ link.user ? link.user.name : "Operador" }}</strong>
                        <span>{{ link.user ? link.user.email : "-" }}</span>
                      </div>
                      <v-btn icon small @click="removeOperator(item, link)">
                        <v-icon size="18">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div v-else class="operator-empty">Nenhum operador vinculado.</div>
                </div>
              </td>
            </template>
            <template v-slot:footer.prepend>
              <v-btn icon small :loading="loadingGatehouses" title="Atualizar" @click="loadGatehouses">
                <v-icon size="19">mdi-refresh</v-icon>
              </v-btn>
            </template>
          </BoaruaDataTable>
        </section>
      </template>
    </div>

    <v-dialog v-model="gatehouseDialog" max-width="620">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ gatehouseForm.id ? "Editar portaria" : "Nova portaria" }}</strong>
            <span>Cadastre ponto de acesso, núcleo e telefone de contato.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="7">
              <v-text-field v-model="gatehouseForm.name" label="Nome da portaria" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="5">
              <v-select v-model="gatehouseForm.contribution_group_id" :items="groupOptions" label="Núcleo" outlined dense clearable hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="gatehouseForm.phone" v-mask="$masks.phone" label="Telefone" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="gatehouseForm.location" label="Localização" outlined dense hide-details="auto" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="gatehouseDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="savingGatehouse" @click="saveGatehouse">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="authorizationRequestDialog" max-width="680">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>Solicitar aprovação</strong>
            <span>Envie uma solicitação ao morador para aprovar ou rejeitar a entrada.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="authorizationRequestForm.visitor_name" label="Nome do visitante/prestador" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="authorizationRequestForm.visit_type" :items="visitTypeOptions" label="Tipo de acesso" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="authorizationRequestForm.unit_id"
                :items="residentUnitOptions"
                label="Residência/unidade"
                outlined
                dense
                clearable
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="authorizationRequestForm.vehicle_plate"
                label="Placa do veículo"
                placeholder="ABC-1234 ou ABC1D23"
                outlined
                dense
                hide-details="auto"
                maxlength="8"
                @input="handleAuthorizationPlateInput"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="authorizationRequestForm.gatehouse_id" :items="gatehouseOptions" label="Portaria" outlined dense clearable hide-details="auto" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="authorizationRequestForm.notes" label="Observação para o morador" outlined rows="3" hide-details="auto" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="authorizationRequestDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="savingAuthorizationRequest" @click="sendAuthorizationRequest">
            Enviar solicitação
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="packageDialog" max-width="680">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ packageForm.id ? "Editar encomenda" : "Nova encomenda" }}</strong>
            <span>Registre o recebimento e a residência para retirada.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="packageForm.recipient_name" label="Nome completo do destinatário" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="packageForm.unit_id"
                :items="unitOptions"
                label="Residência/unidade"
                outlined
                dense
                clearable
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="packageForm.gatehouse_id" :items="gatehouseOptions" label="Portaria" outlined dense clearable hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="packageForm.carrier" label="Transportadora/loja" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="packageForm.tracking_code" label="Código de rastreio" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="packageForm.status" :items="packageStatuses" label="Status" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="packageForm.notes" label="Observação" outlined rows="3" hide-details="auto" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="packageDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="savingPackage" @click="savePackage">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="operatorDialog" max-width="560">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>Adicionar operador</strong>
            <span>Vincule um operador a esta portaria.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="operatorForm.name" label="Nome" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="operatorForm.email" label="E-mail de acesso" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="operatorForm.phone" v-mask="$masks.phone" label="Celular" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="operatorForm.password" label="Senha inicial" outlined dense hide-details="auto" placeholder="boarua123" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="operatorDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="savingOperator" @click="saveOperator">Adicionar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";
import ListFilters from "@/views/boarua/components/ListFilters.vue";
import { applyListFilters, initialListFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaGatehouseCenter",
  components: { BoaruaDataTable, ListFilters },
  data() {
    return {
      loadingGatehouses: false,
      loadingAuthorizations: false,
      loadingPackages: false,
      savingGatehouse: false,
      savingOperator: false,
      savingPackage: false,
      savingAuthorizationRequest: false,
      filterDraft: initialListFilters(),
      activeFilters: initialListFilters(),
      gatehouses: [],
      authorizations: [],
      packages: [],
      groups: [],
      units: [],
      gatehouseDialog: false,
      operatorDialog: false,
      packageDialog: false,
      authorizationRequestDialog: false,
      selectedGatehouse: null,
      gatehouseForm: this.initialGatehouseForm(),
      operatorForm: this.initialOperatorForm(),
      packageForm: this.initialPackageForm(),
      authorizationRequestForm: this.initialAuthorizationRequestForm(),
      visitTypeOptions: [
        { text: "Visitante", value: "visitor" },
        { text: "Prestador", value: "service" },
        { text: "Entrega", value: "delivery" },
        { text: "Motorista", value: "driver" },
      ],
      packageStatuses: [
        { text: "Recebida", value: "received" },
        { text: "Avisada", value: "notified" },
        { text: "Retirada", value: "picked_up" },
      ],
      gatehouseHeaders: [
        { text: "Portaria", value: "name" },
        { text: "Núcleo", value: "contributionGroup" },
        { text: "Operadores", value: "users", sortable: false },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end", width: 96 },
      ],
      authorizationHeaders: [
        { text: "Visitante", value: "visitor_name" },
        { text: "Tipo", value: "visit_type" },
        { text: "Código", value: "access_code" },
        { text: "Morador", value: "member" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end", width: 140 },
      ],
      packageHeaders: [
        { text: "Destinatário", value: "recipient_name" },
        { text: "Unidade", value: "unit" },
        { text: "Transportadora", value: "carrier" },
        { text: "Rastreio", value: "tracking_code" },
        { text: "Recebida em", value: "received_at" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end", width: 120 },
      ],
    };
  },
  computed: {
    activeSection() {
      const section = String(this.$route.params.section || "autorizacoes");
      return ["autorizacoes", "encomendas", "portarias"].includes(section) ? section : "autorizacoes";
    },
    activeSectionMeta() {
      const meta = {
        autorizacoes: {
          title: "Visitantes autorizados",
          description: "Use o código informado pelo morador para registrar o acesso.",
        },
        encomendas: {
          title: "Encomendas recebidas",
          description: "Registre recebimentos e avise a residência correta para retirada.",
        },
        portarias: {
          title: "Portarias",
          description: "Cadastre pontos de acesso e operadores.",
        },
      };

      return meta[this.activeSection] || meta.autorizacoes;
    },
    groupOptions() {
      return this.groups.map((item) => ({ text: item.name, value: item.id }));
    },
    gatehouseOptions() {
      return this.gatehouses.map((item) => ({ text: item.name, value: item.id }));
    },
    unitOptions() {
      return this.units.map((item) => ({ text: this.unitLabel(item), value: item.id }));
    },
    residentUnitOptions() {
      return this.units
        .filter((item) => item.member_id)
        .map((item) => ({ text: this.unitLabel(item), value: item.id }));
    },
    activeLoading() {
      if (this.activeSection === "encomendas") return this.loadingPackages;
      if (this.activeSection === "portarias") return this.loadingGatehouses;
      return this.loadingAuthorizations;
    },
    filterFields() {
      if (this.activeSection === "encomendas") {
        return [
          { name: "status", label: "Status", type: "select", items: this.packageStatuses },
        ];
      }
      if (this.activeSection === "portarias") {
        return [
          { name: "status", label: "Status", type: "select", items: [{ text: "Ativa", value: "active" }, { text: "Inativa", value: "inactive" }] },
          { name: "contribution_group_id", label: "Núcleo", type: "select", items: this.groupOptions },
        ];
      }
      return [
        { name: "status", label: "Status", type: "select", items: [{ text: "Ativa", value: "active" }, { text: "Expirada", value: "expired" }, { text: "Usada", value: "used" }] },
        { name: "visit_type", label: "Tipo", type: "select", items: [{ text: "Visitante", value: "visitor" }, { text: "Prestador", value: "service" }, { text: "Entrega", value: "delivery" }] },
      ];
    },
    filteredAuthorizations() {
      return applyListFilters(this.authorizations, this.activeFilters, {
        textKeys: ["visitor_name", "visit_type", "access_code", "status"],
        dateKeys: ["created_at", "starts_at", "expires_at"],
      });
    },
    filteredPackages() {
      return applyListFilters(this.packages, this.activeFilters, {
        textKeys: ["recipient_name", "carrier", "tracking_code", "status"],
        dateKeys: ["received_at", "created_at", "updated_at"],
      });
    },
    filteredGatehouses() {
      return applyListFilters(this.gatehouses, this.activeFilters, {
        textKeys: ["name", "phone", "location", "status"],
        dateKeys: ["created_at", "updated_at"],
      });
    },
  },
  watch: {
    activeSection() {
      this.resetFilters();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    initialGatehouseForm() {
      return { name: "", contribution_group_id: null, phone: "", location: "", status: "active" };
    },
    initialOperatorForm() {
      return { name: "", email: "", phone: "", password: "" };
    },
    initialPackageForm() {
      return {
        recipient_name: "",
        unit_id: "",
        gatehouse_id: null,
        carrier: "",
        tracking_code: "",
        status: "received",
        notes: "",
      };
    },
    initialAuthorizationRequestForm() {
      return {
        visitor_name: "",
        visit_type: "service",
        unit_id: null,
        gatehouse_id: this.gatehouses[0] ? this.gatehouses[0].id : null,
        vehicle_plate: "",
        notes: "",
      };
    },
    async load() {
      await Promise.all([this.loadGatehouses(), this.loadAuthorizations(), this.loadPackages(), this.loadGroups(), this.loadUnits()]);
    },
    async loadGatehouses() {
      this.loadingGatehouses = true;
      try {
        const response = await axios.get("/gatehouses", { params: { limit: 100 } });
        this.gatehouses = response.data.content.data || response.data.content || [];
      } finally {
        this.loadingGatehouses = false;
      }
    },
    async loadAuthorizations() {
      this.loadingAuthorizations = true;
      try {
        const response = await axios.get("/visitor-authorizations", { params: { limit: 100 } });
        this.authorizations = response.data.content.data || response.data.content || [];
      } finally {
        this.loadingAuthorizations = false;
      }
    },
    async loadPackages() {
      this.loadingPackages = true;
      try {
        const response = await axios.get("/packages", { params: { limit: 100 } });
        this.packages = response.data.content.data || response.data.content || [];
      } finally {
        this.loadingPackages = false;
      }
    },
    async loadGroups() {
      const response = await axios.get("/contribution-groups", { params: { limit: 200 } });
      this.groups = response.data.content.data || response.data.content || [];
    },
    async loadUnits() {
      const response = await axios.get("/member-units", { params: { limit: 300, status: "active" } });
      this.units = response.data.content.data || response.data.content || [];
    },
    resetFilters() {
      this.filterDraft = initialListFilters();
      this.activeFilters = initialListFilters();
    },
    applyFilters(filters) {
      this.activeFilters = Object.assign(initialListFilters(), filters || {});
    },
    openGatehouseDialog(item = null) {
      this.gatehouseForm = item ? Object.assign({}, item) : this.initialGatehouseForm();
      this.gatehouseDialog = true;
    },
    openOperatorDialog(item) {
      this.selectedGatehouse = item;
      this.operatorForm = this.initialOperatorForm();
      this.operatorDialog = true;
    },
    openPackageDialog(item = null) {
      this.packageForm = item ? Object.assign({}, item) : this.initialPackageForm();
      this.packageDialog = true;
    },
    openAuthorizationRequestDialog() {
      this.authorizationRequestForm = this.initialAuthorizationRequestForm();
      this.authorizationRequestDialog = true;
    },
	    async saveGatehouse() {
	      this.savingGatehouse = true;
	      try {
	        const payload = Object.assign({}, this.gatehouseForm, {
	          phone: this.onlyDigits(this.gatehouseForm.phone),
	        });
	        if (!payload.contribution_group_id) payload.contribution_group_id = null;

	        if (this.gatehouseForm.id) {
	          await axios.patch(`/gatehouses/${this.gatehouseForm.id}`, payload);
	        } else {
	          await axios.post("/gatehouses", payload);
	        }
	        this.gatehouseDialog = false;
        await this.loadGatehouses();
        this.notifySuccess("Portaria salva");
      } catch (error) {
        this.notifyError("Não foi possível salvar a portaria");
      } finally {
        this.savingGatehouse = false;
      }
    },
    async saveOperator() {
      if (!this.selectedGatehouse || !this.operatorForm.email) return this.notifyError("Informe o e-mail do operador");

	      this.savingOperator = true;
	      try {
	        const payload = Object.assign({}, this.operatorForm, {
	          phone: this.onlyDigits(this.operatorForm.phone),
	        });
	        await axios.post(`/gatehouses/${this.selectedGatehouse.id}/users`, payload);
	        this.operatorDialog = false;
        await this.loadGatehouses();
        this.notifySuccess("Operador adicionado");
      } catch (error) {
        this.notifyError("Não foi possível adicionar o operador");
      } finally {
        this.savingOperator = false;
      }
    },
    async savePackage() {
      if (!this.packageForm.recipient_name) return this.notifyError("Informe o nome completo do destinatário");
      if (!this.packageForm.unit_id) return this.notifyError("Selecione a residência/unidade");

      this.savingPackage = true;
      try {
        const payload = Object.assign({}, this.packageForm);
        if (!payload.unit_id) payload.unit_id = null;
        delete payload.member_id;
        delete payload.package_type;

        if (payload.id) {
          await axios.patch(`/packages/${payload.id}`, payload);
        } else {
          await axios.post("/packages", payload);
        }
        this.packageDialog = false;
        await this.loadPackages();
        this.notifySuccess("Encomenda salva e disponível para o morador");
      } catch (error) {
        this.notifyError("Não foi possível salvar a encomenda");
      } finally {
        this.savingPackage = false;
      }
    },
    async pickupPackage(item) {
      await axios.post(`/packages/${item.id}/pickup`, {});
      await this.loadPackages();
      this.notifySuccess("Retirada registrada");
    },
    async sendAuthorizationRequest() {
      if (!this.authorizationRequestForm.visitor_name) return this.notifyError("Informe o nome do visitante/prestador");
      const unit = this.units.find((item) => Number(item.id) === Number(this.authorizationRequestForm.unit_id));
      if (!unit || !unit.member_id) return this.notifyError("Selecione uma residência com morador vinculado");

      this.savingAuthorizationRequest = true;
      try {
        await axios.post("/visitor-authorizations", {
          visitor_name: this.authorizationRequestForm.visitor_name,
          visit_type: this.authorizationRequestForm.visit_type,
          unit_id: unit.id,
          member_id: unit.member_id,
          gatehouse_id: this.authorizationRequestForm.gatehouse_id || null,
          vehicle_plate: this.normalizeVehiclePlate(this.authorizationRequestForm.vehicle_plate),
          notes: this.authorizationRequestForm.notes,
          tenant_id: unit.tenant_id,
          association_id: unit.association_id,
          contribution_group_id: unit.contribution_group_id || null,
          approval_required: true,
        });
        this.authorizationRequestDialog = false;
        await this.loadAuthorizations();
        this.notifySuccess("Solicitação enviada ao morador");
      } catch (error) {
        this.notifyError("Não foi possível enviar a solicitação");
      } finally {
        this.savingAuthorizationRequest = false;
      }
    },
    unitLabel(unit) {
      if (!unit) return "-";
      const address = [unit.street, unit.number, unit.complement || unit.apartment || unit.house_number].filter(Boolean).join(", ");
      const group = unit.contributionGroup ? unit.contributionGroup.name : "";
      return [address, group].filter(Boolean).join(" - ") || `Unidade #${unit.id}`;
    },
    async removeOperator(gatehouse, link) {
      await axios.delete(`/gatehouses/${gatehouse.id}/users/${link.id}`);
      await this.loadGatehouses();
      this.notifySuccess("Operador removido");
    },
    async registerAccess(item, direction) {
      await axios.post(`/visitor-authorizations/${item.id}/access`, {
        direction,
        gatehouse_id: this.gatehouses[0] ? this.gatehouses[0].id : null,
      });
      await this.loadAuthorizations();
      this.notifySuccess(direction === "entry" ? "Entrada registrada" : "Saída registrada");
    },
    formatAuthorizationStatus(value) {
      const labels = {
        authorized: "Autorizado",
        pending_approval: "Aguardando morador",
        rejected: "Rejeitado",
        inside: "Dentro do local",
        finished: "Finalizado",
        canceled: "Cancelado",
      };
      return labels[value] || value || "Autorizado";
    },
    handleAuthorizationPlateInput(value) {
      const formatted = this.formatVehiclePlate(value);
      if (formatted !== value) {
        this.authorizationRequestForm.vehicle_plate = formatted;
      }
    },
    formatVehiclePlate(value) {
      const clean = this.normalizeVehiclePlate(value);
      if (/^[A-Z]{3}\d{4}$/.test(clean)) {
        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
      }
      return clean;
    },
    normalizeVehiclePlate(value) {
      return String(value || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 7);
    },
    notifySuccess(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
	    notifyError(message) {
	      this.$dialog && this.$dialog.notify && this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
	    },
	    onlyDigits(value) {
	      return String(value || "").replace(/\D/g, "");
	    },
	  },
	};
</script>

<style scoped lang="scss">
.resource-toolbar,
.panel-heading,
.panel-actions,
.operator-box-header,
.operator-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.resource-toolbar {
  margin-bottom: 14px;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #111827;
    font-size: 20px;
  }

  p {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.tabs-body {
  background: transparent;
}

.table-panel {
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.panel-heading {
  margin-bottom: 14px;

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: #111827;
    font-size: 18px;
  }

  p {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.panel-actions {
  gap: 10px;
}

.expanded-cell {
  padding: 0 !important;
  background: #f7faf8;
}

.operator-box {
  padding: 16px 18px;
  border-top: 1px solid #e3e9e4;
}

.operator-list {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}

.operator-row {
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e3e9e4;
  border-radius: 10px;

  div {
    min-width: 0;
    flex: 1;
  }

  strong,
  span {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
  }
}

.operator-empty {
  margin-top: 10px;
  padding: 14px;
  color: #66736b;
  background: #ffffff;
  border: 1px dashed #d6ded8;
  border-radius: 10px;
  font-size: 13px;
  text-align: center;
}

.status-pill {
  display: inline-flex;
  min-height: 24px;
  padding: 4px 10px;
  align-items: center;
  border-radius: 999px;
  background: #edf7f1;
  color: #08783f;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.status-pending_approval {
  background: #fff7e8;
  color: #9a5b00;
}

.status-rejected,
.status-canceled {
  background: #fdecec;
  color: #b42318;
}

.status-inside {
  background: #e9f2ff;
  color: #175cd3;
}

.muted-action {
  display: inline-flex;
  color: #66736b;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.boarua-table::v-deep th {
  color: #66736b !important;
  font-size: 12px !important;
  text-transform: uppercase;
}
</style>
